import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Package, Cpu, Layers, ArrowLeft, ChevronRight, Download } from 'lucide-react';
import Footer from '../components/Footer';
import { SEO } from '../components/SEO';
import { useReducedMotion } from '../hooks/useReducedMotion';
import { ICE_VERSION, PLATFORMS, PYTHON_VERSIONS, wheels, pythonVersionsFor, wheelFor } from '../data/iceArtifacts';

export default function DownloadPage() {
  const prefersReduced = useReducedMotion();
  const [selectedProduct, setSelectedProduct] = useState(null); // 'ice' or 'goingmerry'
  const [os, setOs] = useState('linux_x86_64');
  const [pythonVersion, setPythonVersion] = useState('310');
  const [edition, setEdition] = useState('community');
  const navigate = useNavigate();
  const { authStatus } = useAuthenticator(context => [context.authStatus]);

  useEffect(() => {
    if (authStatus === 'unauthenticated') {
      navigate('/auth?state=signUp&redirect=/download');
    }
  }, [authStatus, navigate]);

  // Every option comes from the list of wheels that were actually built (src/data/iceCommunityWheels.json)
  const selectedWheel = wheelFor(os, pythonVersion);
  const getDownloadUrl = () => selectedWheel?.url;
  const getFilename = () => selectedWheel?.filename;

  const chooseOs = (nextOs) => {
    setOs(nextOs);
    const available = pythonVersionsFor(nextOs);
    if (!available.includes(pythonVersion)) setPythonVersion(available[0]);
  };

  if (authStatus === 'configuring' || authStatus === 'unauthenticated') {
    return (
      <>
        <SEO title="Download" description="Download ICE, the Indexless Context Engine. GoingMerry is the next product and is not yet available." canonical="https://dopove.com/download" noIndex />
      <div className="bg-[#191d2b] min-h-screen text-white flex flex-col items-center justify-center font-sans">
        <div className={`text-2xl font-mono uppercase tracking-widest text-[#e1dabe] ${prefersReduced ? '' : 'animate-pulse'}`} style={{ animationDuration: prefersReduced ? '0ms' : '300ms' }}>Verifying Node Identity...</div>
      </div>
      </>
    );
  }

  return (
    <>
      <SEO title="Download" description="Download ICE, the Indexless Context Engine. GoingMerry is the next product and is not yet available." canonical="https://dopove.com/download" noIndex />
    <div className={`min-h-screen text-white flex flex-col font-sans transition-colors duration-500 selection:bg-[#6FB6E8]/30 ${
      selectedProduct === 'goingmerry' ? 'bg-[#080B14]' : 'bg-[#191d2b]'
    }`}>
      
      <main className="flex-grow pt-40 pb-20 px-6 relative">
        <div className="max-w-6xl mx-auto">
          
          <AnimatePresence mode="wait">
            {selectedProduct === null && (
              <motion.div
                key="selection"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4 }}
              >
                {/* Header */}
                <div className="mb-16 text-center">
                  <div className="inline-flex items-center gap-3 mb-6 mx-auto px-4 py-2 bg-white/5 border border-white/10 rounded-full">
                    <Package className="w-5 h-5 text-[#e1dabe]" />
                    <span className="text-xs font-black uppercase tracking-[0.3em] text-[#e1dabe]">Product Distribution</span>
                  </div>
                  <h1 className="text-5xl md:text-7xl font-bold tracking-tight mb-6">
                    Choose Product
                  </h1>
                  <p className="text-xl text-slate-400 font-light max-w-2xl leading-relaxed mx-auto">
                    Select the local binary distribution target below. Deploy sovereign context or local runtimes directly on your hardware boundaries.
                  </p>
                </div>

                {/* Selection Cards */}
                <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                  {/* Option A: ICE */}
                  <div
                    onClick={() => setSelectedProduct('ice')}
                    className="group cursor-pointer p-10 rounded-[2.5rem] bg-gradient-to-br from-[#23263a]/40 to-[#191d2b]/20 border border-white/10 hover:border-[#e1dabe]/40 hover:bg-[#23263a]/60 hover-lift focus-visible-ring transition-[border-color,background-color,transform,box-shadow] duration-200 ease-[var(--ease-out)] relative flex flex-col justify-between shadow-xl"
                  >
                    <div>
                      <div className="w-14 h-14 bg-gradient-to-br from-amber-500/10 to-amber-500/20 border border-amber-500/20 rounded-2xl flex items-center justify-center mb-8 text-amber-400 group-hover:scale-105 transition-transform">
                        <Cpu className="w-7 h-7" />
                      </div>
                      <h3 className="text-3xl font-bold mb-2 text-white tracking-tight">ICE 3.1</h3>
                      <span className="text-xs font-mono tracking-widest text-[#e1dabe] block mb-4 uppercase">Indexless Context Engine</span>
                      <p className="text-slate-400 font-light text-sm leading-relaxed mb-8">
                        ICE wraps your OpenAI or Anthropic client to automatically handle embedding, storage, and memory retrieval under the hood. No databases or indexes required.
                      </p>
                    </div>
                    <div className="flex items-center justify-between border-t border-white/5 pt-6 mt-auto">
                      <span className="text-xs font-mono text-[#e1dabe] font-bold flex items-center gap-1.5">
                        Download Now
                      </span>
                      <ChevronRight className="w-5 h-5 text-slate-400 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>

                  {/* Option B: GoingMerry */}
                  <div
                    onClick={() => setSelectedProduct('goingmerry')}
                    className="group cursor-pointer p-10 rounded-[2.5rem] bg-gradient-to-br from-[#101A2B]/80 to-[#080B14]/40 border border-white/10 hover:border-[#62B7FF]/40 hover:bg-[#101A2B] hover-lift focus-visible-ring transition-[border-color,background-color,transform,box-shadow] duration-200 ease-[var(--ease-out)] relative flex flex-col justify-between shadow-xl"
                  >
                    <div>
                      <div className="w-14 h-14 bg-gradient-to-br from-[#62B7FF]/10 to-[#62B7FF]/20 border border-[#62B7FF]/20 rounded-2xl flex items-center justify-center mb-8 text-[#62B7FF] group-hover:scale-105 transition-transform">
                        <Layers className="w-7 h-7" />
                      </div>
                      <h3 className="text-3xl font-bold mb-2 text-white tracking-tight">GoingMerry</h3>
                      <span className="text-xs font-mono tracking-widest text-[#62B7FF] block mb-4 uppercase">Local AI Runtime</span>
                      <p className="text-slate-400 font-light text-sm leading-relaxed mb-8">
                        High-performance local execution runtime for open weights models.
                      </p>
                    </div>
                    <div className="flex items-center justify-between border-t border-white/5 pt-6 mt-auto">
                      <span className="text-xs font-mono text-slate-500 flex items-center gap-1.5">
                        Coming Soon
                      </span>
                      <ChevronRight className="w-5 h-5 text-slate-400 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* ICE PRODUCT DETAIL */}
            {selectedProduct === 'ice' && (
              <motion.div
                key="ice-detail"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4 }}
              >
                {/* Back button */}
                <button 
                  onClick={() => setSelectedProduct(null)}
                  className="flex items-center gap-2 text-xs font-mono text-slate-400 hover:text-white mb-10 transition-colors uppercase tracking-widest"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Product Selection
                </button>

                {/* Title */}
                <div className="mb-12">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="p-2 bg-[#e1dabe]/10 rounded-lg border border-[#e1dabe]/20 text-[#e1dabe]">
                      <Cpu className="w-5 h-5" />
                    </div>
                    <span className="text-xs font-black uppercase tracking-[0.3em] text-[#e1dabe]">ICE Distributions</span>
                  </div>
                  <h1 className="text-5xl md:text-7xl font-bold tracking-tight mb-4">ICE 3.1</h1>
                </div>

                {/* Interactive Downloader */}
                <div className="p-8 sm:p-12 rounded-[2.5rem] bg-gradient-to-br from-[#23263a]/40 to-[#191d2b]/20 border border-white/10 relative overflow-hidden backdrop-blur-md shadow-2xl">
                  <div className="absolute top-0 right-0 w-[300px] h-[300px] bg-amber-500/5 rounded-full filter blur-[80px] pointer-events-none" />
                  <div className="absolute bottom-0 left-0 w-[200px] h-[200px] bg-[#e1dabe]/5 rounded-full filter blur-[60px] pointer-events-none" />

                  <div className="relative z-10 grid lg:grid-cols-2 gap-12 items-stretch">
                    {/* Left: Interactive Selectors */}
                    <div className="space-y-8 flex flex-col justify-between">
                      {/* OS Selector */}
                      <div>
                        <span className="text-[10px] font-black uppercase tracking-widest text-[#e1dabe] block mb-3">1. Select Target OS</span>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                          {PLATFORMS.map((item) => (
                            <button
                              key={item.id}
                              onClick={() => chooseOs(item.id)}
                              className={`px-3 py-3 rounded-xl border text-[11px] font-bold hover-scale-press focus-visible-ring transition-[background-color,border-color,box-shadow,transform] duration-200 ease-[var(--ease-out)] ${
                                os === item.id 
                                  ? 'bg-[#e1dabe] text-[#191d2b] border-[#e1dabe] shadow-lg shadow-[#e1dabe]/10' 
                                  : 'bg-white/5 border-white/10 hover:border-white/20 text-slate-300'
                              }`}
                            >
                              {item.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Python Version Selector */}
                      <div>
                        <span className="text-[10px] font-black uppercase tracking-widest text-[#e1dabe] block mb-3">2. Select Python Version</span>
                        <div className="flex gap-2">
                          {PYTHON_VERSIONS.map((item) => (
                            <button
                              key={item.id}
                              disabled={!pythonVersionsFor(os).includes(item.id)}
                              onClick={() => setPythonVersion(item.id)}
                              className={`px-4 py-3 rounded-xl border text-xs font-bold hover-scale-press focus-visible-ring transition-[background-color,border-color,box-shadow,transform] duration-200 ease-[var(--ease-out)] flex-1 disabled:opacity-30 disabled:cursor-not-allowed ${
                                pythonVersion === item.id 
                                  ? 'bg-[#e1dabe] text-[#191d2b] border-[#e1dabe] shadow-lg shadow-[#e1dabe]/10' 
                                  : 'bg-white/5 border-white/10 hover:border-white/20 text-slate-300'
                              }`}
                            >
                              {item.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Edition Selector */}
                      <div>
                        <span className="text-[10px] font-black uppercase tracking-widest text-[#e1dabe] block mb-3">3. Select Edition</span>
                        <div className="flex gap-2">
                          {[
                            { id: 'community', label: 'Free Edition' },
                            { id: 'enterprise', label: 'Enterprise Edition' }
                          ].map((item) => (
                            <button
                              key={item.id}
                              onClick={() => setEdition(item.id)}
                              className={`px-4 py-3 rounded-xl border text-xs font-bold hover-scale-press focus-visible-ring transition-[background-color,border-color,box-shadow,transform] duration-200 ease-[var(--ease-out)] flex-1 ${
                                edition === item.id 
                                  ? 'bg-[#e1dabe] text-[#191d2b] border-[#e1dabe] shadow-lg shadow-[#e1dabe]/10' 
                                  : 'bg-white/5 border-white/10 hover:border-white/20 text-slate-300'
                              }`}
                            >
                              {item.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Right: Download Info & Actions */}
                    <div className="p-8 rounded-3xl bg-black/30 border border-white/5 flex flex-col justify-between space-y-6">
                      {edition === 'community' ? (
                        <>
                          <div>
                            <span className="text-[10px] font-black uppercase tracking-widest text-slate-500 block mb-2">Build Target Details</span>
                            <h4 className="text-xl font-bold text-white mb-2 uppercase tracking-tight">
                              ICE Free Edition
                            </h4>
                            <div className="space-y-2 mt-4 font-mono text-[11px] text-slate-400">
                              <div className="flex justify-between py-1.5 border-b border-white/5">
                                <span>OS Platform:</span>
                                <span className="text-white uppercase">{PLATFORMS.find((p) => p.id === os)?.label}</span>
                              </div>
                              <div className="flex justify-between py-1.5 border-b border-white/5">
                                <span>Python Target:</span>
                                <span className="text-white">v3.{pythonVersion.slice(1)}</span>
                              </div>
                              <div className="flex justify-between py-1.5 border-b border-white/5">
                                <span>Version:</span>
                                <span className="text-white">{ICE_VERSION}</span>
                              </div>
                              <div className="flex justify-between py-1.5 border-b border-white/5">
                                <span>Artifact Name:</span>
                                <span className="text-slate-300 break-all text-right max-w-[200px]">{getFilename()}</span>
                              </div>
                            </div>
                          </div>

                          <div className="space-y-4">
                            <a
                              href={getDownloadUrl()}
                              className="w-full py-4 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 rounded-2xl text-xs font-black uppercase tracking-widest text-slate-950 flex items-center justify-center gap-2 shadow-lg shadow-amber-500/10 hover:shadow-amber-500/20 hover-scale-press focus-visible-ring transition-[background-color,transform,box-shadow] duration-200 ease-[var(--ease-out)] cursor-pointer decoration-none font-bold"
                            >
                              <Download className="w-4 h-4" />
                              Download Python Wheel
                            </a>

                            <div className="mt-4 p-4 rounded-xl bg-black/40 border border-white/5 text-left">
                              <span className="text-[9px] font-black uppercase tracking-widest text-slate-500 block mb-2">Terminal Installation</span>
                              <pre className="text-[10px] text-emerald-400 font-mono overflow-x-auto leading-relaxed select-all whitespace-pre-wrap break-all">
                                pip install {getDownloadUrl()}
                              </pre>
                            </div>

                            <button
                              onClick={() => setSelectedProduct(null)}
                              className="w-full py-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-2xl text-xs font-black uppercase tracking-widest text-slate-300 hover:text-white hover-scale-press focus-visible-ring transition-[background-color,border-color,transform] duration-200 ease-[var(--ease-out)]"
                            >
                              Return to List
                            </button>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="flex-grow flex flex-col justify-center py-6 text-left">
                            <span className="text-[10px] font-black uppercase tracking-widest text-amber-400 block mb-4">Enterprise Custom Calibrations</span>
                            <h4 className="text-2xl font-bold text-white mb-4 uppercase tracking-tight font-sans">
                              ICE Enterprise
                            </h4>
                            <p className="text-slate-400 font-light text-xs leading-relaxed mb-4">
                              Enterprise binaries are custom compiled and calibrated to your target hardware limits, private telemetry sinks, and strict SLA requirements. 
                            </p>
                            <p className="text-slate-400 font-light text-xs leading-relaxed">
                              Enterprise builds are delivered directly following commercial alignment. Request a calibration build below.
                            </p>
                          </div>

                          <div className="space-y-4">
                            <button
                              onClick={() => window.location.href = '/contact'}
                              className="w-full py-4 bg-gradient-to-r from-[#e1dabe] to-[#c2bca8] hover:from-[#c2bca8] hover:to-[#e1dabe] rounded-2xl text-xs font-black uppercase tracking-widest text-slate-950 flex items-center justify-center gap-2 shadow-lg shadow-[#e1dabe]/10 hover:shadow-[#e1dabe]/20 hover-scale-press focus-visible-ring transition-[background-color,transform,box-shadow] duration-200 ease-[var(--ease-out)] font-bold"
                            >
                              Contact Sales for Access
                            </button>

                            <button
                              onClick={() => setSelectedProduct(null)}
                              className="w-full py-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-2xl text-xs font-black uppercase tracking-widest text-slate-300 hover:text-white hover-scale-press focus-visible-ring transition-[background-color,border-color,transform] duration-200 ease-[var(--ease-out)]"
                            >
                              Return to List
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Every Community artifact */}
                <section className="mt-16" aria-labelledby="all-downloads-heading">
                  <h2 id="all-downloads-heading" className="text-2xl font-bold tracking-tight mb-2">All Community downloads</h2>
                  <p className="text-sm text-slate-400 mb-6">ICE {ICE_VERSION}, Community edition. Install a wheel with <code className="font-mono text-slate-300">pip install</code> and its address.</p>
                  <div className="overflow-x-auto rounded-2xl border border-white/10">
                    <table aria-label="All Community downloads" className="w-full text-sm text-left">
                      <thead className="bg-white/5 text-xs uppercase tracking-widest text-slate-400">
                        <tr>
                          <th scope="col" className="px-4 py-3">Platform</th>
                          <th scope="col" className="px-4 py-3">Python</th>
                          <th scope="col" className="px-4 py-3">File</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-white/5">
                        {PLATFORMS.flatMap((platform) =>
                          wheels()
                            .filter((w) => w.platform === platform.id)
                            .map((w) => (
                              <tr key={w.filename}>
                                <td className="px-4 py-3 text-slate-300 whitespace-nowrap">{platform.label}</td>
                                <td className="px-4 py-3 text-slate-300 whitespace-nowrap">3.{w.python.slice(1)}</td>
                                <td className="px-4 py-3">
                                  <a href={w.url} className="font-mono text-xs text-[#e1dabe] hover:underline break-all focus-visible-ring">
                                    {w.filename}
                                  </a>
                                </td>
                              </tr>
                            ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </section>
              </motion.div>
            )}

            {/* GOINGMERRY PRODUCT DETAIL */}
            {selectedProduct === 'goingmerry' && (
              <motion.div
                key="merry-detail"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4 }}
              >
                {/* Back button */}
                <button 
                  onClick={() => setSelectedProduct(null)}
                  className="flex items-center gap-2 text-xs font-mono text-slate-400 hover:text-white mb-10 transition-colors uppercase tracking-widest"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Product Selection
                </button>

                {/* Title */}
                <div className="mb-12">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="p-2 bg-[#62B7FF]/10 rounded-lg border border-[#62B7FF]/20 text-[#62B7FF]">
                      <Layers className="w-5 h-5" />
                    </div>
                    <span className="text-xs font-black uppercase tracking-[0.3em] text-[#62B7FF]">Local Runtimes</span>
                  </div>
                  <h1 className="text-5xl md:text-7xl font-bold tracking-tight mb-4">GoingMerry</h1>
                </div>

                {/* Main panel */}
                <div className="p-12 rounded-[2.5rem] bg-gradient-to-br from-[#101A2B]/85 to-[#080B14]/40 border border-white/5 relative overflow-hidden backdrop-blur-md shadow-2xl">
                  {/* Glowing background highlights */}
                  <div className="absolute top-0 right-0 w-[300px] h-[300px] bg-[#62B7FF]/5 rounded-full filter blur-[80px] pointer-events-none" />
                  <div className="absolute bottom-0 left-0 w-[200px] h-[200px] bg-[#8FD4FF]/5 rounded-full filter blur-[60px] pointer-events-none" />

                  <div className="max-w-2xl mx-auto text-center relative z-10 py-8">
                    <div className="w-20 h-20 rounded-[2rem] bg-gradient-to-br from-[#62B7FF]/10 to-[#62B7FF]/20 border border-[#62B7FF]/20 flex items-center justify-center mx-auto mb-8 shadow-lg shadow-[#62B7FF]/5 relative">
                      <div className="absolute inset-0 rounded-[2rem] bg-[#62B7FF]/20 filter blur-md opacity-50 animate-pulse-reduced" />
                      <svg className="w-8 h-8 text-[#62B7FF] relative z-10 animate-spin-reduced" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.43l-1.003.828c-.293.241-.438.613-.43.992a7.723 7.723 0 010 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.222-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.43l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.991l-1.004-.827a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.645-.869l.214-1.28z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>

                    <h2 className="text-3xl font-bold tracking-tight text-white mb-4">GoingMerry Staging & Compiler Calibration</h2>
                    <p className="text-[#62B7FF] font-black uppercase tracking-[0.2em] text-[10px] mb-8 bg-[#62B7FF]/10 border border-[#62B7FF]/20 px-4 py-1.5 rounded-full inline-block">
                      Coming Soon / Final Calibration In Progress
                    </p>

                    <div className="space-y-6 text-slate-400 text-sm font-light leading-relaxed max-w-xl mx-auto">
                      <p>
                        To ensure full compatibility with modern local hardware boundaries, custom GPU kernel drivers, and advanced model sharding Swarm frameworks, our local runtime distributions are currently undergoing final compiler calibration.
                      </p>
                      <p>
                        We are standardizing native execution targets across Windows, macOS, and Linux to prevent optimization friction. Local download channels are temporarily undergoing maintenance during this calibration cycle.
                      </p>
                    </div>

                    <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4">
                      <button
                        onClick={() => setSelectedProduct(null)}
                        className="w-full sm:w-auto px-8 py-4 bg-white/5 hover:bg-white/10 border border-white/10 rounded-2xl text-xs font-black uppercase tracking-widest text-slate-300 hover:text-white hover-scale-press focus-visible-ring transition-[background-color,border-color,transform] duration-200 ease-[var(--ease-out)]"
                      >
                        Return to List
                      </button>
                      <button
                        onClick={() => window.location.href = '/profile'}
                        className="w-full sm:w-auto px-8 py-4 bg-gradient-to-r from-[#62B7FF] to-[#8FD4FF] hover:from-[#8FD4FF] hover:to-[#62B7FF] rounded-2xl text-xs font-black uppercase tracking-widest text-slate-950 font-black shadow-lg shadow-[#62B7FF]/10 hover:shadow-[#62B7FF]/20 hover-scale-press focus-visible-ring transition-[background-color,transform,box-shadow] duration-200 ease-[var(--ease-out)]"
                      >
                        Check Waitlist Status
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

        </div>
      </main>



      <Footer />
    </div>
    </>
  );
}
